import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import CalendarHeader from './CalendarHeader';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';

const CALENDAR_VIEWS = {
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day'
};

const Calendar = () => {
  const theme = useTheme();
  const { isDark } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedGarage } = useGarage();

  // Calendar state
  const [currentView, setCurrentView] = useState(CALENDAR_VIEWS.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date()); // Use today's date
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showSidebar, setShowSidebar] = useState(false);
  
  // Data state
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);


  // Fetch appointments function for refresh callback
  const fetchAppointments = async () => {
    if (!selectedGarage?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    
    // Start timer for minimum loading time
    const startTime = Date.now();
    const minLoadingTime = 500; // Minimum 500ms to show loading
    
    try {
      let startDate, endDate;
      
      // Calculate date range based on current view
      if (currentView === CALENDAR_VIEWS.MONTH) {
        const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        startDate = start.getFullYear() + '-' + String(start.getMonth() + 1).padStart(2, '0') + '-' + String(start.getDate()).padStart(2, '0');
        endDate = end.getFullYear() + '-' + String(end.getMonth() + 1).padStart(2, '0') + '-' + String(end.getDate()).padStart(2, '0');
      } else if (currentView === CALENDAR_VIEWS.WEEK) {
        const start = getWeekStart(currentDate);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        startDate = start.getFullYear() + '-' + String(start.getMonth() + 1).padStart(2, '0') + '-' + String(start.getDate()).padStart(2, '0');
        endDate = end.getFullYear() + '-' + String(end.getMonth() + 1).padStart(2, '0') + '-' + String(end.getDate()).padStart(2, '0');
      } else {
        // Day view
        startDate = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0') + '-' + String(currentDate.getDate()).padStart(2, '0');
        endDate = startDate;
      }

      const response = await apiService.getBookings({
        garage_id: selectedGarage.id,
        start_date: startDate,
        end_date: endDate
      });

      const bookingsList = response.data?.bookings || [];
      
      // Transform API data to calendar format
      const transformedAppointments = bookingsList.map(booking => ({
        id: booking.booking_id,
        title: `${booking.vehicle_regno} - ${booking.customer_name}`,
        date: booking.booking_date,
        startTime: booking.booking_time,
        endTime: booking.booking_end_time > booking.booking_time 
          ? booking.booking_end_time 
          : calculateEndTime(booking.booking_time),
        vehicleReg: booking.vehicle_regno,
        customerName: booking.customer_name,
        services: [
          ...booking.products.map(p => ({ name: p.name, category: p.category })),
          ...booking.addons.map(a => ({ name: a.name, category: a.category }))
        ],
        products: booking.products,
        addons: booking.addons,
        categories: booking.categories, // Categories from backend
        status: booking.status,
        paymentStatus: booking.payment_status,
        totalAmount: booking.total_amount,
        phone: booking.phone,
        email: booking.email,
        // Keep original booking data for modals
        originalBooking: booking
      }));

      setAppointments(transformedAppointments);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to load appointments');
    } finally {
      // Ensure minimum loading time has passed before hiding spinner
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }
  };

  // Fetch garage settings (includes products and addons)
  const fetchGarageSettings = async () => {
    if (!selectedGarage?.id) return;
    
    try {
      const response = await apiService.getGarageSettings(selectedGarage.id);
      const settings = response.data || response;
      
      // Store garage settings for business hours validation
      setGarageSettings(settings);
      
      const enabledProducts = (settings.selected_products || []).filter(product => product.is_enabled).map(product => ({
        ...product,
        id: product.product_id
      }));
      const enabledAddons = (settings.selected_addons || []).filter(addon => addon.is_enabled).map(addon => ({
        ...addon,
        id: addon.addon_id
      }));
      
      setAvailableProducts(enabledProducts);
      setAvailableAddons(enabledAddons);
    } catch (error) {
      console.error('Error fetching garage settings:', error);
      setGarageSettings(null);
      setAvailableProducts([]);
      setAvailableAddons([]);
    }
  };

  // Helper function to calculate end time (assume 30 minutes if not provided)
  const calculateEndTime = (startTime) => {
    if (!startTime) return null;
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 30; // Add 30 minutes
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  // Get category counts from backend data - count individual services, not appointments
  const getCategoryCounts = (appointments) => {
    const counts = { MOT: 0, Services: 0, Valeting: 0, Repairs: 0, Others: 0 };
    
    appointments.forEach(appointment => {
      // Count products by category
      if (appointment.products) {
        appointment.products.forEach(product => {
          const category = product.category || 'Others';
          if (counts.hasOwnProperty(category)) {
            counts[category]++;
          }
        });
      }
      
      // Count addons by category
      if (appointment.addons) {
        appointment.addons.forEach(addon => {
          const category = addon.category || 'Others';
          if (counts.hasOwnProperty(category)) {
            counts[category]++;
          }
        });
      }
    });
    
    return counts;
  };

  // Business hours validation functions
  const [garageSettings, setGarageSettings] = useState(null);

  // Check if a date/time is within business hours
  const isWithinBusinessHours = (date, time = null) => {
    if (!garageSettings?.opening_hours) return true; // Allow if no settings

    // Get day name in short format (mon, tue, wed, etc.)
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
    const dayHours = garageSettings.opening_hours[dayName];
    
    // If no hours defined for this day or marked as closed, consider it closed
    if (!dayHours || dayHours === 'closed') {
      return false;
    }

    // If no specific time provided, just check if day is open
    if (!time) {
      return true;
    }

    // Parse the "09:00-18:00" format
    const [openTime, closeTime] = dayHours.split('-');
    
    if (time < openTime || time >= closeTime) {
      return false;
    }

    // Check lunch hours if enabled
    const lunchHours = garageSettings.lunch_hours;
    if (lunchHours?.enabled && lunchHours.start && lunchHours.end) {
      if (time >= lunchHours.start && time < lunchHours.end) {
        return false;
      }
    }

    return true;
  };

  // Check if a date is a business day (has opening hours)
  const isBusinessDay = (date) => {
    if (!garageSettings?.opening_hours) return true; // Allow if no settings

    // Get day name in short format (mon, tue, wed, etc.)
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
    const dayHours = garageSettings.opening_hours[dayName];
    
    return dayHours && dayHours !== 'closed';
  };

  // Helper function to get start of week (Monday)
  const getWeekStart = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday
    start.setDate(diff);
    return start;
  };

  // Navigation functions
  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (currentView === CALENDAR_VIEWS.MONTH) {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (currentView === CALENDAR_VIEWS.WEEK) {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (currentView === CALENDAR_VIEWS.MONTH) {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (currentView === CALENDAR_VIEWS.WEEK) {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // View change handler
  const handleViewChange = (view) => {
    setCurrentView(view);
  };


  // Fetch appointments when dependencies change
  useEffect(() => {
    if (selectedGarage?.id) {
      fetchAppointments();
    } else {
      // If no garage selected, set loading to false
      setLoading(false);
      setAppointments([]);
    }
  }, [selectedGarage?.id, currentDate.getTime(), currentView]);

  // Fetch garage settings when garage changes (only once per garage)
  useEffect(() => {
    if (selectedGarage?.id) {
      fetchGarageSettings();
    }
  }, [selectedGarage?.id]);

  // Get current view title
  const getViewTitle = () => {
    const options = { 
      month: 'long', 
      year: 'numeric',
      ...(currentView === CALENDAR_VIEWS.DAY && { 
        weekday: 'long',
        day: 'numeric'
      })
    };

    if (currentView === CALENDAR_VIEWS.WEEK) {
      const weekStart = getWeekStart(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${weekStart.toLocaleDateString('en-GB', { day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      } else {
        return `${weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
    }

    return currentDate.toLocaleDateString('en-GB', options);
  };



  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Calendar Header */}
      <CalendarHeader
        title={getViewTitle()}
        currentView={currentView}
        onViewChange={handleViewChange}
        onPrevious={navigatePrevious}
        onNext={navigateNext}
        onToday={navigateToday}
        isMobile={isMobile}
        currentDate={currentDate}
        onDateChange={setCurrentDate}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Calendar Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          bgcolor: isDark ? 'grey.900' : 'grey.50'
        }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography>Loading appointments...</Typography>
            </Box>
          )}

          {error && !loading && (
            <Box sx={{ p: 3 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {!loading && !error && (
            <>
              {currentView === CALENDAR_VIEWS.MONTH && (
                <MonthView
                  currentDate={currentDate}
                  selectedDate={selectedDate}
                  appointments={appointments}
                  onDateSelect={setSelectedDate}
                  onDateChange={setCurrentDate}
                  getCategoryCounts={getCategoryCounts}
                  availableProducts={availableProducts}
                  availableAddons={availableAddons}
                  isWithinBusinessHours={isWithinBusinessHours}
                  isBusinessDay={isBusinessDay}
                  onRefreshCalendar={fetchAppointments}
                />
              )}
              
              {currentView === CALENDAR_VIEWS.WEEK && (
                <WeekView
                  currentDate={currentDate}
                  selectedDate={selectedDate}
                  appointments={appointments}
                  onDateSelect={setSelectedDate}
                  getCategoryCounts={getCategoryCounts}
                  availableProducts={availableProducts}
                  availableAddons={availableAddons}
                  isWithinBusinessHours={isWithinBusinessHours}
                  isBusinessDay={isBusinessDay}
                  onRefreshCalendar={fetchAppointments}
                />
              )}
              
              {currentView === CALENDAR_VIEWS.DAY && (
                <DayView
                  currentDate={currentDate}
                  appointments={appointments}
                  getCategoryCounts={getCategoryCounts}
                  availableProducts={availableProducts}
                  availableAddons={availableAddons}
                  isWithinBusinessHours={isWithinBusinessHours}
                  isBusinessDay={isBusinessDay}
                  onRefreshCalendar={fetchAppointments}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Calendar;